import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { PORTFOLIO as FALLBACK } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Portfolio() {
  const [items, setItems] = useState(FALLBACK);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/portfolio`);
        if (!cancelled && Array.isArray(data) && data.length > 0) {
          setItems(data);
        }
      } catch {
        // Silent fallback to static list
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Choose feature item + secondary items
  const feature = items[0];
  const rest = items.slice(1, 5); // up to 4 secondary

  return (
    <section id="portfolio" data-testid="portfolio-section" className="relative bg-white py-20 md:py-32">
      <div className="px-5 sm:px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14 items-end">
          <div className="lg:col-span-7">
            <div className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.25em] sm:tracking-[0.3em] text-sunrise">— Recent work</div>
            <h2 className="mt-4 font-display font-black text-navy tracking-tight text-[32px] sm:text-5xl lg:text-6xl leading-[1.04]">
              Real projects, <br />real Utah families &amp; businesses.
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 text-muted-foreground text-base leading-relaxed">
            A small selection of recent residential remodels, commercial build-outs and ground-up construction we&apos;ve
            completed. Want to see something similar to your project? Just ask.
          </div>
        </div>

        {loading && items.length === 0 ? (
          <div className="grid place-items-center py-24 text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {feature && (
              <a
                href="#contact"
                data-testid="portfolio-item-0"
                className="group relative md:col-span-8 md:row-span-2 overflow-hidden bg-navy rounded-2xl elev-2"
              >
                <img
                  src={feature.url}
                  alt={feature.title}
                  className="w-full h-[340px] sm:h-[420px] md:h-[640px] object-cover group-hover:scale-[1.03] transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/85 via-navy-deep/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-10 text-white">
                  <div className="text-xs uppercase tracking-[0.25em] text-sunrise">{feature.category}</div>
                  <h3 className="mt-3 font-display font-bold text-3xl lg:text-4xl">{feature.title}</h3>
                </div>
              </a>
            )}

            {rest.map((p, idx) => {
              const i = idx + 1;
              const layout = layoutFor(i, rest.length);
              return (
                <a
                  key={p.id || p.url}
                  href="#contact"
                  data-testid={`portfolio-item-${i}`}
                  className={`group relative ${layout.cls} overflow-hidden bg-navy rounded-2xl elev-2`}
                >
                  <img
                    src={p.url}
                    alt={p.title}
                    className={`w-full ${layout.h} object-cover group-hover:scale-[1.03] transition-transform duration-700`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/85 via-navy-deep/10 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8 text-white flex items-end justify-between">
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.25em] text-sunrise">{p.category}</div>
                      <h3 className="mt-2 font-display font-bold text-xl lg:text-2xl">{p.title}</h3>
                    </div>
                    <span className="w-10 h-10 grid place-items-center border border-white/70 rounded-lg group-hover:bg-sunrise group-hover:border-sunrise transition-colors">
                      <ArrowUpRight className="w-4 h-4" />
                    </span>
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function layoutFor(i, total) {
  // i is 1-based among secondary items (total up to 4)
  if (total <= 2) return { cls: "md:col-span-4", h: "h-[240px] sm:h-[300px]" };
  if (total === 3) {
    if (i === 1 || i === 2) return { cls: "md:col-span-4", h: "h-[240px] sm:h-[300px]" };
    return { cls: "md:col-span-12", h: "h-[300px] sm:h-[420px]" };
  }
  // total >= 4
  if (i === 1 || i === 2) return { cls: "md:col-span-4", h: "h-[240px] sm:h-[300px]" };
  return { cls: "md:col-span-6", h: "h-[300px] sm:h-[420px]" };
}
